import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bot, X, Send, User, Mic, Volume2, VolumeX, Layout as LayoutIcon, MessageSquare, CheckCircle2, FileText, Shield, Zap, Maximize2, Minimize2, Copy, Edit2, ArrowDown, Square, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { isUrl, scrapePropertyUrl, formatLeadSummary, getMissingFields, type ParsedLeadData } from '../lib/ai/lead-importer';
import { FormattedContent } from './FormattedContent';
import { useStore } from '../store/useStore';
import toast from 'react-hot-toast';
import { ConfirmModal } from './ConfirmModal';
import { AIOnboardingModal } from './modals/AIOnboardingModal';
import {
  hasUserApiKey,
  processPrompt,
  generatePageInsights
} from '../lib/gemini';
import { isSupabaseConfigured } from '../lib/supabase';
import { SaveLeadModal } from './SaveLeadModal';
import { RateLimitModal } from './RateLimitModal';
import { voiceService } from '../lib/voice-service';
import { usageTracker, UsageData } from '../lib/usage-tracking';
import { UsageLimitModal } from './UsageLimitModal';
import {
  executeTask,
  generateResponse,
  TaskExecutor
} from '../lib/local-ai';
import {
  saveMessage,
  getMemory,
  syncUserProfile,
  loadHistory,
  setActivePage,
  setLearnedIntents
} from '../lib/local-ai/memory-store';
import { AIBotLearningButtons } from './AIBotLearningButtons';
import { intents } from '../lib/ai/intents';
import {
  saveConversationTurn,
  generateSessionId,
  getUserPreferences,
  getAllLearnedIntents,
  LearnedIntent,
  saveLearnedIntent
} from '../lib/local-ai/learning-service';

interface ChatMessage {
  id: string;
  role: 'user' | 'ai';
  content: string;
  timestamp: string;
  intent?: string;
  data?: unknown;
  systemLog?: string;
  type?: 'learning-buttons' | 'default';
  originalPhrase?: string;
}

// FormattedContent moved to shared component

export interface BotResponse {
  intent: string;
  response: string;
  data?: any;
  nextIntent?: { name: string; params: any };
  systemLog?: string;
}

interface DisambiguationData {
  originalIntent: {
    intent: {
      action: string;
    };
    params: any;
  };
}

export function AIBotWidget() {
  const [pendingLeadImport, setPendingLeadImport] = useState<ParsedLeadData | null>(null);
  const [prompt, setPrompt] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [hasKey, setHasKey] = useState<boolean | null>(null);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const {
    isAiOpen, setAiOpen,
    currentUser, showFloatingAIWidget,
    aiModel, isAiDocked, setAiDocked,
    isAiFullScreen, setAiFullScreen,
    sidebarOpen, aiName, onboarding_ai_choice_seen,
    setOnboardingAiChoiceSeen,
    isAiGenerating, setAiGenerating, aiDisplayedText, setAiDisplayedText,
    typingMessageId: storeTypingMessageId, setTypingMessageId: setStoreTypingMessageId,
    aiMessages, addAiMessage, clearAiThreadMessages, setAiMessagesForThread
  } = useStore();

  const messages = useMemo(() => aiMessages['widget'] || [], [aiMessages]);
  const [speechEnabled, setSpeechEnabled] = useState(() => voiceService.isSpeechEnabled());
  const [isSpeaking, setIsSpeaking] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem('ai_widget_position');
    return saved ? JSON.parse(saved) : { x: 24, y: 24 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [matchCount, setMatchCount] = useState(0);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [lastUserPrompt, setLastUserPrompt] = useState('');
  const [bars, setBars] = useState<number[]>(new Array(20).fill(5));
  const [insights, setInsights] = useState<string[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Fetch usage on mount and after every response
  const fetchUsage = async () => {
    const timeout = setTimeout(() => {
      console.warn('[🤖 AI Widget] fetchUsage timed out after 5s');
    }, 5000);

    try {
      const data = await usageTracker.getUsage();
      setUsage(data);
    } catch (err) {
      console.error('Failed to fetch AI usage:', err);
    } finally {
      clearTimeout(timeout);
    }
  };

  useEffect(() => {
    fetchUsage();
  }, []);

  // Simulate voice bars animation
  useEffect(() => {
    if (isRecording || isSpeaking) {
      const interval = setInterval(() => {
        setBars(new Array(20).fill(0).map(() => Math.random() * 40 + 10));
      }, 100);
      return () => clearInterval(interval);
    } else {
      setBars(new Array(20).fill(5));
    }
  }, [isRecording, isSpeaking]);

  // v12.0: Active Page Context Tracking
  useEffect(() => {
    const path = location.pathname;
    if (path.length > 1) {
      console.log(`[🤖 OS BOT] Syncing context for page: ${path}`);
      setActivePage(path);
      
      // Proactive Insight Generation
      const generateInsights = async () => {
        setInsightsLoading(true);
        try {
          const res = await generatePageInsights(path, {});
          setInsights(Array.isArray(res) ? res : []);
        } catch (err) {
          console.error('[🤖 Insights] Failed to generate:', err);
          setInsights([]);
        } finally {
          setInsightsLoading(false);
        }
      };
      generateInsights();
    }
  }, [location.pathname]);

  // SMS Session State — collects target + message without repeat-asking


  const [sessionId, setSessionId] = useState<string>('');

  useEffect(() => {
    const initSession = async () => {
      const storeSessionId = useStore.getState().osBotSessionId;
      let id = storeSessionId || localStorage.getItem('os_bot_session_id');
      if (!id) {
        id = generateSessionId();
        localStorage.setItem('os_bot_session_id', id);
        useStore.getState().setOsBotSessionId(id);
      } else if (!storeSessionId) {
        useStore.getState().setOsBotSessionId(id);
      }
      setSessionId(id);

      // Load user preferences and Learned Intents
      const userId = currentUser?.id;
      if (userId) {
        // 1. Load Preferences
        const prefs = await getUserPreferences(userId);
        if (prefs?.preferred_name) {
          setUserName(prefs.preferred_name);
        }

        // 2. Hydrate Neural Learning
        console.log('[🤖 OS BOT] Synchronizing Neural Intents from Supabase...');
        const learned = await getAllLearnedIntents(userId);
        if (learned && learned.length > 0) {
          console.log(`[🤖 OS BOT] Hydrated ${learned.length} learned patterns.`);
          setLearnedIntents(learned.map((l: LearnedIntent) => ({
            phrase: l.phrase,
            intent: l.mapped_intent,
            params: l.params
          })));
        }
      }
    };
    initSession();
  }, [currentUser?.id]);

  // Auto-switch to offline mode when disconnected
  useEffect(() => {
    const handleOffline = () => {
      const { offlineModelReady, setOfflineMode } = useStore.getState();
      if (offlineModelReady) {
        setOfflineMode(true);
        toast('Network disconnected. Switching to Offline AI.', { icon: '🔌' });
      } else {
        toast.error('Network disconnected. Cloud AI unavailable.');
      }
    };
    
    window.addEventListener('offline', handleOffline);
    return () => window.removeEventListener('offline', handleOffline);
  }, []);

  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
  });

  const [saveContactModal, setSaveContactModal] = useState<{
    isOpen: boolean;
    phone: string;
  }>({
    isOpen: false,
    phone: '',
  });

  const [pendingTypoSuggestion, setPendingTypoSuggestion] = useState<{ suggestedText: string, keyword: string } | null>(null);

  // Handle Dragging
  const startDrag = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      posX: position.x,
      posY: position.y
    };
    // Don't prevent default if it's a click on text/buttons, 
    // but the header is mostly empty space.
  };

  useEffect(() => {
    if (!isDragging) return;

    const onMouseMove = (e: MouseEvent) => {
      const deltaX = dragStart.current.x - e.clientX;
      const deltaY = dragStart.current.y - e.clientY;

      const newPos = {
        x: Math.max(20, Math.min(window.innerWidth - 60, dragStart.current.posX + deltaX)),
        y: Math.max(20, Math.min(window.innerHeight - 60, dragStart.current.posY + deltaY))
      };
      setPosition(newPos);
    };

    const onMouseUp = (e: MouseEvent) => {
      setIsDragging(false);
      localStorage.setItem('ai_widget_position', JSON.stringify(position));
      // If dragged near the top of screen, auto-dock
      if (e.clientY < 80) {
        setAiDocked(true);
        setAiOpen(false);
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging, position]);

  // Load preferences
  useEffect(() => {
    async function loadPrefs() {
      if (!currentUser?.id) return;

      const keyExists = await hasUserApiKey();
      setHasKey(keyExists);

      // Show onboarding if no keys and haven't seen it yet
      if (keyExists === false && !onboarding_ai_choice_seen) {
        setShowOnboarding(true);
      }

      if (isSupabaseConfigured) {
        // Fetch usage data
        await fetchUsage();

        // Sync user profile and load history from Supabase
        syncUserProfile(currentUser.id);
        
        const storeSessionId = useStore.getState().osBotSessionId;
        let sId = storeSessionId || localStorage.getItem('os_bot_session_id');
        if (!sId) {
          sId = generateSessionId();
          localStorage.setItem('os_bot_session_id', sId);
          useStore.getState().setOsBotSessionId(sId);
        } else if (!storeSessionId) {
          useStore.getState().setOsBotSessionId(sId);
        }
        setSessionId(sId);
        await loadHistory(currentUser.id, sId);

        // Load combined history from memory store after sync
        const memory = getMemory();
        if (memory?.history && Array.isArray(memory.history)) {
          const chatHistory: ChatMessage[] = memory.history.map((m, i) => ({
            id: `hist-${i}-${m.timestamp}`,
            role: m.role === 'assistant' ? 'ai' : 'user',
            content: m.content,
            timestamp: m.timestamp,
            intent: m.intent,
            systemLog: "🤖 OS Bot · Local"
          }));
          setAiMessagesForThread('widget', chatHistory);
        } else {
          setAiMessagesForThread('widget', [{
            id: 'welcome',
            role: 'ai',
            content: `Hi there! I'm 🤖 OS Bot. How can I help you on the ${location.pathname.split('/').pop() || 'dashboard'} today?`,
            timestamp: new Date().toISOString(),
            systemLog: "🤖 OS Bot · Local"
          }]);
        }
      }
    }
    loadPrefs();

    window.addEventListener('ai-settings-updated', loadPrefs);
    return () => window.removeEventListener('ai-settings-updated', loadPrefs);
  }, [currentUser?.id, onboarding_ai_choice_seen]);

  // Handle outside toggle events
  useEffect(() => {
    const handleToggle = () => setAiOpen(!isAiOpen);
    const handleClear = () => clearAiThreadMessages('widget');
    const handleUndock = () => {
      setAiDocked(false);
      setAiOpen(true);
    };

    const handleDock = () => {
      setAiDocked(true);
      setAiOpen(false);
    };

    window.addEventListener('toggle-ai-widget', handleToggle);
    window.addEventListener('clear-ai-chat', handleClear);
    window.addEventListener('undock-ai-widget', handleUndock);
    window.addEventListener('dock-ai-widget', handleDock);

    // Ctrl+Shift+V shortcut
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        setAiOpen(!isAiOpen);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('toggle-ai-widget', handleToggle);
      window.removeEventListener('clear-ai-chat', handleClear);
      window.removeEventListener('undock-ai-widget', handleUndock);
      window.removeEventListener('dock-ai-widget', handleDock);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Initialize Voice Service Callbacks
  useEffect(() => {
    voiceService.setCallbacks(
      (transcript) => setPrompt(prev => prev + transcript),
      (isRec) => {
        setIsRecording(isRec);
      },
      (err) => {
        console.warn(err);
      }
    );
  }, []);

  // Real-time search effect for highlighting and scrolling matching keywords
  useEffect(() => {
    if (!showSearch || !searchQuery || searchQuery.trim().length < 2) {
      setMatchCount(0);
      setCurrentMatchIndex(0);
      return;
    }

    // Delay slightly to allow DOM to render highlights
    const timer = setTimeout(() => {
      const matches = document.querySelectorAll('.search-match');
      setMatchCount(matches.length);
      
      // Update active highlight classes and scroll into view
      matches.forEach((el, index) => {
        if (index === currentMatchIndex) {
          el.classList.add('bg-orange-500', 'border-orange-600', 'text-white', 'scale-105', 'shadow-md', 'z-50');
          el.classList.remove('bg-[var(--t-warning)]/30', 'border-[var(--t-warning)]/50');
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          el.classList.remove('bg-orange-500', 'border-orange-600', 'scale-105', 'shadow-md', 'z-50');
          el.classList.add('bg-[var(--t-warning)]/30', 'border-[var(--t-warning)]/50');
        }
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [searchQuery, currentMatchIndex, showSearch, messages]);

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    setCurrentMatchIndex(0);
  };

  const handleNextMatch = () => {
    if (matchCount === 0) return;
    setCurrentMatchIndex((prev) => (prev + 1) % matchCount);
  };

  const handlePrevMatch = () => {
    if (matchCount === 0) return;
    setCurrentMatchIndex((prev) => (prev - 1 + matchCount) % matchCount);
  };

  // Use a ref for the prompt so we can submit it inside an effect without staleness
  const promptRef = useRef(prompt);
  useEffect(() => {
    promptRef.current = prompt;
  }, [prompt]);

  // Handle auto-submit on recording end
  useEffect(() => {
    if (!isRecording && promptRef.current.trim().length > 0 && !loading) {
      const dummyEvent = { preventDefault: () => { } } as React.FormEvent;
      handleSubmit(dummyEvent);
    }
  }, [isRecording]);

  const toggleRecording = () => {
    voiceService.toggleListening();
  };

  const speak = (text: string) => {
    voiceService.speak(
      text,
      () => setIsSpeaking(true),
      () => setIsSpeaking(false)
    );
  };

  useEffect(() => {
    voiceService.toggleMute(!speechEnabled);
  }, [speechEnabled]);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (autoScrollEnabled && (isAiGenerating || messages.length > 0)) {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [messages, aiDisplayedText, isAiGenerating, autoScrollEnabled]);

  // Fix 1: Proper scroll to bottom logic
  useEffect(() => {
    if (isAiOpen) {
      const scrollToBottom = () => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (scrollRef.current) {
              scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
              console.log('[OSBot] Scrolled to bottom (Widget Open/Message Update)');
            }
          }, 50);
        });
      };
      scrollToBottom();
    }
  }, [isAiOpen, messages.length]);

  // Mount useEffect for initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        console.log('[OSBot] Scrolled to bottom (Initial Mount)');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    // Standard stick-to-bottom pattern: check if scrolled within 10px of bottom
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
    setAutoScrollEnabled(isAtBottom);
    setShowScrollButton(!isAtBottom && scrollHeight > clientHeight);
  };

  const handleCopyMessage = (content: string, id: string) => {
    navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleEditMessage = (content: string) => {
    setPrompt(content);
  };

  // Message persistence handled by global store

  // Typing Animation logic moved to handleSubmit

  const abortControllerRef = useRef<AbortController | null>(null);

  const handleStopGeneration = () => {
    console.log('[🤖 OS BOT] Stopping generation...');
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setAiGenerating(false);
    setStoreTypingMessageId(null);
    setLoading(false);
    toast.success('Generation stopped');
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    // Fix 2: Set interaction flag at the very start
    const { setHasInteractedWithAI } = useStore.getState();
    setHasInteractedWithAI(true);
    
    e?.preventDefault();

    if (isAiGenerating || loading) {
      handleStopGeneration();
      return;
    }

    let userText = prompt.trim();
    
    // Resume logic: if input is empty and we have a last prompt, re-trigger it
    if (!userText && lastUserPrompt) {
      userText = lastUserPrompt;
      console.log('[🤖 OS BOT] Resuming/Retrying last prompt:', userText);
    }

    if (!userText) return;
    
    setLastUserPrompt(userText);
    setPrompt('');
    
    const store = useStore.getState();
    const storeProvider = store.preferred_api_provider || 'local';
    
    // 1. Onboarding check
    if (!hasKey && aiModel !== 'os-bot') {
       setShowOnboarding(true);
       return;
    }

    // 2. Usage limits check
    if (usage && usage.remaining <= 0) {
      setShowUsageModal(true);
      return;
    }

    if (userText.toLowerCase().match(/^(yes|yeah|yep|yup|sure|ok|okay|proceed|confirm)$/i)) {
      if (pendingTypoSuggestion?.suggestedText) {
        userText = pendingTypoSuggestion.suggestedText;
        setPendingTypoSuggestion(null);
      }
    }

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: userText,
      timestamp: new Date().toISOString()
    };

    if (currentUser?.id) saveMessage(currentUser.id, sessionId, 'user', userText);

    if (sessionId && currentUser?.id) {
      await saveConversationTurn(currentUser.id, sessionId, 'user', userText);
    }

    addAiMessage('widget', userMsg);
    setLoading(true);

    // Create new abort controller for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      // ── URL / Property Import Detection ─────────────────────────────────────
      if (isUrl(userText)) {
        console.log('[🤖 OS BOT] URL detected — attempting property scrape:', userText);
        const scrapeResult = await scrapePropertyUrl(userText);
        if (scrapeResult.success && scrapeResult.data) {
          setPendingLeadImport(scrapeResult.data);
          const missing = getMissingFields(scrapeResult.data);
          const summary = formatLeadSummary(scrapeResult.data, missing);
          const botMsg: ChatMessage = {
            id: (Date.now() + 1).toString(),
            role: 'ai',
            content: `🏠 **Property Found!**\n\n${summary}`,
            timestamp: new Date().toISOString(),
            systemLog: '🤖 OS Bot'
          };
          addAiMessage('widget', botMsg);
          if (currentUser?.id) saveMessage(currentUser.id, sessionId, 'assistant', botMsg.content);
          setLoading(false);
          return;
        } else {
          // Scrape failed — show error message instead of sending to AI
          const errorMsg: ChatMessage = {
            id: (Date.now() + 1).toString(),
            role: 'ai',
            content: `⚠️ Couldn't extract property data from that URL. ${scrapeResult.errorMsg || ''}\n\nYou can try pasting the property details as text instead.`,
            timestamp: new Date().toISOString(),
            systemLog: '🤖 OS Bot'
          };
          addAiMessage('widget', errorMsg);
          setLoading(false);
          return;
        }
      }

      // ── "yes" confirmation to create lead ────────────────────────────────────
      const isYes = /^(yes|yeah|yep|yup|sure|ok|okay|create|confirm|do it|add it|save it)$/i.test(userText.trim().replace(/[!.?]$/, ''));
      if (pendingLeadImport && isYes) {
        const lead = pendingLeadImport;
        setPendingLeadImport(null);
        try {
          const store = useStore.getState();
          const result = await store.addLead({
            name: lead.name || 'Unknown Owner',
            email: lead.email || '',
            phone: lead.phone || '',
            propertyAddress: lead.propertyAddress || '',
            city: lead.city || '',
            state: lead.state || '',
            zip: lead.zip || '',
            estimatedValue: lead.estimatedValue || 0,
            bedrooms: lead.bedrooms || 0,
            bathrooms: lead.bathrooms || 0,
            sqft: lead.sqft || 0,
            agentName: lead.agentName || '',
            agentPhone: lead.agentPhone || '',
            notes: lead.notes || '',
            status: 'new',
            source: 'website',
            propertyType: 'single-family',
            leadScore: 50,
            dealScore: 50,
            tags: [],
            customFields: {},
            timeline: [],
            statusHistory: [],
          } as any);
          const confirmMsg: ChatMessage = {
            id: (Date.now() + 1).toString(),
            role: 'ai',
            content: result.success
              ? `✅ **Lead created!** ${lead.propertyAddress || 'Property'} has been added to your CRM. You can find it in the Leads section.`
              : `❌ Failed to create lead: ${result.error?.message || 'Unknown error'}`,
            timestamp: new Date().toISOString(),
            systemLog: '🤖 OS Bot'
          };
          addAiMessage('widget', confirmMsg);
          if (currentUser?.id) saveMessage(currentUser.id, sessionId, 'assistant', confirmMsg.content);
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Unknown error';
          const errMsg: ChatMessage = {
            id: (Date.now() + 1).toString(),
            role: 'ai',
            content: `❌ Failed to create lead: ${msg}`,
            timestamp: new Date().toISOString(),
          };
          addAiMessage('widget', errMsg);
        }
        setLoading(false);
        return;
      }

      // ── "no" — cancel pending lead import ────────────────────────────────────
      const isNo = /^(no|nope|cancel|skip|nevermind|never mind|don't|stop|ignore)$/i.test(userText.trim().replace(/[!.?]$/, ''));
      if (pendingLeadImport && isNo) {
        setPendingLeadImport(null);
        const cancelMsg: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'ai',
          content: 'Got it — lead import cancelled. Let me know if you need anything else!',
          timestamp: new Date().toISOString(),
        };
        addAiMessage('widget', cancelMsg);
        setLoading(false);
        return;
      }

      // 3. THE UNIFIED GATEWAY (Local -> Neural -> Global)
      console.log('[🤖 OS BOT] Unified Pipeline Start:', userText);
      const gatewayResponse = await processPrompt(userText, {
        page: location.pathname,
        sessionId,
        source: 'user'
      }, storeProvider || aiModel, controller.signal);

      // standard usage tracker increments (Supabase messages table)
      await usageTracker.incrementUsage();

      // 4. Handle Special Intents
      if (gatewayResponse.intent === 'rate_limit') {
        const finalId = (Date.now() + 1).toString();
        const content = gatewayResponse.response || "You've reached your AI limit for today. Please upgrade or try again later.";
        addAiMessage('widget', {
          id: finalId,
          role: 'ai',
          content,
          timestamp: new Date().toISOString(),
          systemLog: "🤖 OS Bot"
        });
        saveMessage(currentUser?.id || 'system', sessionId, 'assistant', content);
        
        // Background Sync & Typing
        setAiGenerating(true);
        setStoreTypingMessageId(finalId);
        
        let currentText = "";
        const words = content.split(" ");
        for (let i = 0; i < words.length; i++) {
          if (controller.signal.aborted) break;
          currentText += (i === 0 ? "" : " ") + words[i];
          setAiDisplayedText(currentText);
          await new Promise(r => setTimeout(r, 20));
        }
        
        setAiGenerating(false);
        setStoreTypingMessageId(null);
        setAiDisplayedText('');
        return;
      }

      if (gatewayResponse.intent === 'redirect_setup') {
        const finalId = (Date.now() + 1).toString();
        const content = "I need your OS Cloud Key to work. Redirecting you to settings...";
        addAiMessage('widget', {
          id: finalId,
          role: 'ai',
          content,
          timestamp: new Date().toISOString(),
          systemLog: "🤖 OS Bot"
        });
        saveMessage(currentUser?.id || 'system', sessionId, 'assistant', content);
        
        // Background Sync & Typing
        setAiGenerating(true);
        setStoreTypingMessageId(finalId);
        
        let currentText = "";
        const words = content.split(" ");
        for (let i = 0; i < words.length; i++) {
          if (controller.signal.aborted) break;
          currentText += (i === 0 ? "" : " ") + words[i];
          setAiDisplayedText(currentText);
          await new Promise(r => setTimeout(r, 20));
        }
        
        setAiGenerating(false);
        setStoreTypingMessageId(null);
        setAiDisplayedText('');
        setTimeout(() => navigate('/settings'), 1500);
        return;
      }

      // 5. Normal AI Response Display
      const aiResponseId = (Date.now() + 1).toString();
      addAiMessage('widget', {
        id: aiResponseId,
        role: 'ai',
        content: gatewayResponse.response,
        timestamp: new Date().toISOString(),
        intent: gatewayResponse.intent,
        data: gatewayResponse.data,
        systemLog: gatewayResponse.systemLog,
        type: gatewayResponse.intent === 'unknown' ? 'learning-buttons' : 'default',
        originalPhrase: (gatewayResponse.intent === 'unknown') ? userText : undefined
      });

      if (sessionId && currentUser?.id) {
        await saveConversationTurn(currentUser.id, sessionId, 'assistant', gatewayResponse.response);
      }
      if (currentUser?.id) saveMessage(currentUser.id, sessionId, 'assistant', gatewayResponse.response);

      // Background Sync & Typing
      setAiGenerating(true);
      setStoreTypingMessageId(aiResponseId);
      
      let currentText = "";
      const words = gatewayResponse.response.split(" ");
      for (let i = 0; i < words.length; i++) {
        if (controller.signal.aborted || !abortControllerRef.current) break;
        currentText += (i === 0 ? "" : " ") + words[i];
        setAiDisplayedText(currentText);
        await new Promise(r => setTimeout(r, 20));
      }
      
      setAiGenerating(false);
      setStoreTypingMessageId(null);
      setAiDisplayedText('');

      // Voice Follow-up
      if (gatewayResponse.intent !== 'help' && !gatewayResponse.systemLog?.includes('Teaching')) {
        speak(gatewayResponse.response);
      }

      // Chain Next Intents (if any)
      if (gatewayResponse.nextIntent) {
        setTimeout(() => {
          handleExecuteAction(gatewayResponse.nextIntent!.name, gatewayResponse.nextIntent!.params);
        }, 1000);
      }

      // Navigation Fallback
      if (gatewayResponse.intent === 'navigate' && gatewayResponse.data?.path) {
        navigate(gatewayResponse.data.path);
      }

    } catch (err: any) {
      console.error('[🤖 OS BOT] Submission Failure:', err);
      
      let errorMsg = "I encountered a problem processing that. Let's try again or ask for 'help'.";
      
      if (err.message) {
        if (err.message.includes('401')) {
          errorMsg = `Authentication Error: Your API key for ${useStore.getState().preferred_api_provider} is invalid or expired. Please check your AI Settings.`;
        } else if (err.message.includes('429')) {
          errorMsg = `Rate Limit: Your ${useStore.getState().preferred_api_provider} account has reached its quota or rate limit. Try switching providers in Settings.`;
        } else if (err.message.includes('404')) {
          errorMsg = `Model Error: The selected AI model for ${useStore.getState().preferred_api_provider} was not found. We've logged this for the dev team.`;
        } else {
          errorMsg = `Error: ${err.message}`;
        }
      }

      addAiMessage('widget', {
        id: Date.now().toString(),
        role: 'ai',
        content: errorMsg,
        timestamp: new Date().toISOString(),
        systemLog: "🤖 OS Bot"
      });
    } finally {
      setLoading(false);
      setAiGenerating(false);
      setStoreTypingMessageId(null);
    }
  };

  const handleLearnIntent = async (originalPhrase: string, intent: string, params: Record<string, any> = {}) => {
    const userId = currentUser?.id;
    if (!userId) return;

    await saveLearnedIntent(userId, originalPhrase, intent, params);
    console.log(`[🤖 OS Bot] Learned: "${originalPhrase}" → ${intent}`);

    const intentObj = intents.find(i => i.name === intent);
    if (intentObj) {
      const result = await executeTask(intentObj.action, { ...params });
      const response = generateResponse(intentObj, result, originalPhrase);

      const botMsg: ChatMessage = {
        id: Date.now().toString(),
        role: 'ai',
        content: response,
        timestamp: new Date().toISOString(),
        systemLog: "🤖 OS Bot"
      };
      addAiMessage('widget', botMsg);
      
      // Background Sync & Typing
      setAiGenerating(true);
      setStoreTypingMessageId(botMsg.id);
      
      let currentText = "";
      const words = response.split(" ");
      for (let i = 0; i < words.length; i++) {
        currentText += (i === 0 ? "" : " ") + words[i];
        setAiDisplayedText(currentText);
        await new Promise(r => setTimeout(r, 20));
      }
      
      setAiGenerating(false);
      setStoreTypingMessageId(null);
      setAiDisplayedText('');
    }
  };

  const handleExecuteAction = async (intent: string, data: any) => {
    let result: { success: boolean; message: string; data?: any } | null = null;

    try {
      // 1. Domain Action Normalization (v11.0 Legacy Support)
      let domainAction = intent;
      let actionParams = { ...data };

      if (intent === 'create_task') { domainAction = 'task_action'; actionParams.action = 'create_task'; }
      else if (intent === 'update_status') { domainAction = 'crm_action'; actionParams.action = 'update_status'; actionParams.status = data.newStatus; }
      else if (intent === 'create_lead') { domainAction = 'crm_action'; actionParams.action = 'create_lead'; }
      else if (intent === 'update_lead') { domainAction = 'crm_action'; actionParams.action = 'update_lead'; }
      else if (intent === 'delete_lead') { domainAction = 'crm_action'; actionParams.action = 'delete_lead'; }
      else if (intent === 'send_sms') { domainAction = 'comms_action'; actionParams.action = 'send_sms'; }

      // 2. Centralized Execution (v11.0)
      if (domainAction === 'confirm_action') {
        const underlyingIntent = data?.intent;
        if (underlyingIntent && underlyingIntent !== 'confirm_action') {
          return handleExecuteAction(underlyingIntent, data);
        } else {
          result = { success: false, message: 'Invalid confirmation target.' };
        }
      } else {
        setLoading(true);
        const taskResponse = await TaskExecutor.execute(domainAction, actionParams);
        result = {
          success: taskResponse.success,
          message: taskResponse.message,
          data: taskResponse.data
        };
        setLoading(false);
      }

      // Increment usage if successful
      await usageTracker.incrementUsage();
      await fetchUsage();

      const finalId = Date.now().toString();
      addAiMessage('widget', {
        id: finalId,
        role: 'ai',
        content: result
          ? (result.success ? result.message : `❌ ${result.message}`)
          : 'Action completed.',
        timestamp: new Date().toISOString(),
        intent: intent,
        systemLog: "🤖 OS Bot"
      });
      
      // Background Sync & Typing
      setAiGenerating(true);
      setStoreTypingMessageId(finalId);
      
      const speechText = result
        ? (result.success ? result.message : `Error. ${result.message}`)
        : 'Action completed.';
      
      let currentText = "";
      const words = speechText.split(" ");
      for (let i = 0; i < words.length; i++) {
        currentText += (i === 0 ? "" : " ") + words[i];
        setAiDisplayedText(currentText);
        await new Promise(r => setTimeout(r, 20));
      }
      
      setAiGenerating(false);
      setStoreTypingMessageId(null);
      setAiDisplayedText('');
      speak(speechText);
    } catch (err: any) {
      setLoading(false);
      const errId = (Date.now() + 1).toString();
      const errorMessage = err?.message || "Something went wrong.";
      const helpfulError = errorMessage.toLowerCase().includes('google') || errorMessage.toLowerCase().includes('sms')
        ? `❌ ${errorMessage}. Please check your connection in Settings.`
        : `❌ I couldn't complete that action. This usually happens if a service is temporarily offline. Try again in a moment!`;

      addAiMessage('widget', {
        id: errId,
        role: 'ai',
        content: helpfulError,
        timestamp: new Date().toISOString(),
        systemLog: "🤖 OS Bot"
      });
      
      // Background Sync & Typing
      setAiGenerating(true);
      setStoreTypingMessageId(errId);
      
      let currentText = "";
      const words = helpfulError.split(" ");
      for (let i = 0; i < words.length; i++) {
        currentText += (i === 0 ? "" : " ") + words[i];
        setAiDisplayedText(currentText);
        await new Promise(r => setTimeout(r, 20));
      }
      
      setAiGenerating(false);
      setStoreTypingMessageId(null);
      setAiDisplayedText('');
    }
  };

  const handleCloseOnboarding = () => {
    setShowOnboarding(false);
    setOnboardingAiChoiceSeen(true);
  };

  const handleConfigureCloud = () => {
    handleCloseOnboarding();
    navigate('/settings/ai');
  };

  if (!showFloatingAIWidget) return null;

  return (
    <div
      className={`fixed z-[var(--z-loader)] flex items-end pointer-events-none ${position.y > window.innerHeight / 2 ? 'flex-col' : 'flex-col-reverse'
        }`}
      style={{ bottom: `${position.y}px`, right: `${position.x}px` }}
    >

      {/* Expanded Chat Window */}
      {isAiOpen && (
        <div
          className={`border rounded-2xl shadow-2xl flex flex-col overflow-hidden pointer-events-auto 
            ${isAiFullScreen 
              ? 'fixed inset-4 w-auto h-auto z-[300] !mb-0' 
              : `w-80 md:w-96 h-[500px] ${position.y > window.innerHeight / 2 ? 'order-last mt-4 mb-0 origin-top-right' : 'mb-4 origin-bottom-right'}`
            }`}
          style={{
            background: 'var(--t-surface)',
            borderColor: 'var(--t-border)',
            ...(isAiFullScreen ? {
              position: 'fixed',
              top: '1rem',
              left: '1rem',
              right: '1rem',
              bottom: '1rem'
            } : {
              position: 'absolute',
              bottom: position.y > window.innerHeight / 2 ? 'auto' : '100%',
              top: position.y > window.innerHeight / 2 ? '100%' : 'auto',
              right: 0
            })
          }}
        >

          {/* Header */}
          <div
            className={`p-3 flex items-center justify-between cursor-move ${position.y < 200 ? 'order-last border-t rounded-b-2xl' : 'border-b rounded-t-2xl'}`}
            style={{ background: 'var(--t-surface-hover)', borderColor: 'var(--t-border)' }}
            onMouseDown={startDrag}
          >
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setAiDocked(true); setAiOpen(false); }}
                className="w-6 h-6 rounded-lg flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
                style={{ background: 'var(--t-primary)' }}
                title="Dock to bookshelf"
              >
                <Bot className="w-4 h-4" style={{ color: 'var(--t-on-primary)' }} />
              </button>
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5">
                  <h2 className="text-xs font-black text-[var(--t-text)] leading-none">
                    {userName ? `${aiName} | ${userName}` : aiName}
                  </h2>
                  {currentUser?.preferred_api_provider !== 'local' && (
                    <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-500/10 border border-blue-500/20">
                      <Zap className="w-2.5 h-2.5 text-blue-500" />
                      <span className="text-[8px] font-bold text-blue-500 uppercase tracking-tighter">
                        {currentUser?.preferred_api_provider || 'Premium'}
                      </span>
                    </div>
                  )}
                  {currentUser?.preferred_api_provider === 'local' && (
                    <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--t-success)]/10 border border-[var(--t-success)]/20">
                      <Shield className="w-2.5 h-2.5 text-[var(--t-success)]" />
                      <span className="text-[8px] font-bold text-[var(--t-success)] uppercase tracking-tighter">Local</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setSpeechEnabled(!speechEnabled)}
                className={`p-1.5 rounded-lg transition-colors group ${speechEnabled ? 'text-[var(--t-primary)]' : 'text-[var(--t-text-muted)]'}`}
                title={speechEnabled ? "Mute AI Voice" : "Enable AI Voice"}
              >
                {speechEnabled ? (
                  <Volume2 size={16} className={isSpeaking ? 'animate-pulse' : ''} />
                ) : (
                  <VolumeX size={16} />
                )}
              </button>
              
              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSearch(!showSearch);
                  if (!showSearch) {
                    setSearchQuery('');
                  }
                }}
                className={`p-1.5 hover:bg-black/10 rounded-lg transition-colors group ${showSearch ? 'text-[var(--t-primary)] bg-black/5' : ''}`}
                title="Search conversation"
              >
                <Search size={16} className={showSearch ? 'text-[var(--t-primary)]' : 'text-[var(--t-text-muted)] group-hover:text-[var(--t-primary)]'} />
              </button>

              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setAiFullScreen(!isAiFullScreen);
                }}
                className="p-1.5 hover:bg-black/10 rounded-lg transition-colors group"
                title={isAiFullScreen ? "Restore" : "Full Screen"}
              >
                {isAiFullScreen ? (
                  <Minimize2 size={16} className="text-[var(--t-text-muted)] group-hover:text-[var(--t-primary)]" />
                ) : (
                  <Maximize2 size={16} className="text-[var(--t-text-muted)] group-hover:text-[var(--t-primary)]" />
                )}
              </button>

              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setAiDocked(true);
                  setAiOpen(false);
                }}
                className="p-1.5 hover:bg-black/10 rounded-lg transition-colors group"
                title="Dock to top bar"
              >
                <LayoutIcon size={16} className="text-[var(--t-text-muted)] group-hover:text(--t-primary)" />
              </button>
              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setAiOpen(false);
                }}
                className="p-1.5 hover:bg-black/10 rounded-lg transition-colors group"
                title="Close"
              >
                <X size={16} className="text-[var(--t-text-muted)] group-hover:text-[var(--t-error)]" />
              </button>
            </div>
          </div>

          {showSearch && (
            <div className="p-2 border-b flex items-center gap-2 bg-[var(--t-surface-hover)]" style={{ borderColor: 'var(--t-border)' }}>
              <div className="relative flex-1">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search conversation..."
                  className="w-full pl-8 pr-2 py-1 text-xs rounded-lg focus:outline-none focus:ring-1 focus:ring-[var(--t-primary)] bg-[var(--t-bg)] border border-[var(--t-input-border)] text-[var(--t-text)]"
                />
              </div>
              {matchCount > 0 && (
                <span className="text-[10px] text-[var(--t-text-muted)] font-mono whitespace-nowrap">
                  {currentMatchIndex + 1}/{matchCount}
                </span>
              )}
              <div className="flex items-center gap-0.5">
                <button
                  onClick={handlePrevMatch}
                  disabled={matchCount === 0}
                  className="p-1 rounded hover:bg-black/10 disabled:opacity-30 text-[var(--t-text)]"
                  title="Previous match"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  onClick={handleNextMatch}
                  disabled={matchCount === 0}
                  className="p-1 rounded hover:bg-black/10 disabled:opacity-30 text-[var(--t-text)]"
                  title="Next match"
                >
                  <ChevronDown size={14} />
                </button>
                <button
                  onClick={() => {
                    setShowSearch(false);
                    setSearchQuery('');
                    setMatchCount(0);
                    setCurrentMatchIndex(0);
                  }}
                  className="p-1 rounded hover:bg-black/10 text-[var(--t-text)]"
                  title="Close search"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          )}

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className={`flex-1 overflow-y-auto p-4 space-y-4 ${position.y < 200 ? 'order-1' : ''}`}
            style={{ 
              background: 'var(--t-background)',
              color: 'var(--t-text)'
            }}
          >
            {/* Proactive Insights */}
            {Array.isArray(insights) && insights.length > 0 && (
              <div className="bg-[var(--t-primary)]/5 border border-[var(--t-primary)]/10 rounded-2xl p-3 mb-2 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--t-primary)] flex items-center gap-1.5">
                    <Bot size={12} /> Proactive Insights
                  </span>
                  {insightsLoading && (
                    <div className="flex-1 mt-2">
                      {null}
                    </div>
                  )}
                </div>
                {/* Voice Visualization Bars */}
                {(isRecording || isSpeaking) && (
                  <div className="flex items-end justify-center gap-1 h-8 mb-4">
                    {bars.map((height, i) => (
                      <div
                        key={i}
                        className="w-1 rounded-full bg-[var(--t-primary)] opacity-60 transition-all duration-100"
                        style={{ height: `${height * 0.6}px` }}
                      />
                    ))}
                  </div>
                )}

                {/* Messages */}
                <div className="space-y-4">
                  {insights.map((insight: string, i: number) => (
                    <div key={i} className="flex gap-2 items-start text-[11px] text-[var(--t-text)] leading-snug">
                      <div className="w-1 h-1 rounded-full bg-[var(--t-primary)] mt-1.5 shrink-0" />
                      <p>{insight}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {messages?.map((msg) => {
              if (msg.type === 'learning-buttons') {
                return (
                  <AIBotLearningButtons
                    key={msg.id}
                    originalPhrase={msg.originalPhrase || ''}
                    onSelect={(intent) => handleLearnIntent(msg.originalPhrase || '', intent)}
                    onDismiss={() => setAiMessagesForThread('widget', (prev) => prev.filter(m => m.id !== msg.id))}
                  />
                );
              }

              return (
                <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 shadow-md ${msg.role !== 'user' ? 'AIBotWidget-bot-avatar' : ''}`}
                    style={{ 
                      background: msg.role === 'user' ? 'var(--t-primary)' : '#4f46e5', // High contrast indigo for bot
                      border: msg.role === 'user' ? 'none' : '1px solid rgba(255, 255, 255, 0.2)' 
                    }}>
                    {msg.role === 'user' ? (
                      <User className="w-3 h-3" style={{ color: '#ffffff' }} />
                    ) : (
                      <Bot className="w-3 h-3" style={{ color: '#ffffff' }} /> // Force white icon on dark background
                    )}
                  </div>
                  <div className={`max-w-[85%] ${msg.role === 'user' ? 'items-end' : 'items-start'} flex flex-col group/msg relative`}>
                    <div className={`px-4 py-3 rounded-[1.5rem] text-xs leading-relaxed shadow-lg relative ${msg.role === 'user' ? 'rounded-tr-none bg-[var(--t-primary)] text-[var(--t-on-primary)]' : 'border rounded-tl-none bg-[var(--t-surface)] border-[var(--t-border)] text-[var(--t-text)]'
                      }`}>
                      
                      {/* Message Actions */}
                      <div className={`absolute -top-2 ${msg.role === 'user' ? '-left-8' : '-right-8'} flex gap-1 opacity-0 group-hover/msg:opacity-100 transition-opacity z-10`}>
                        <button
                          type="button"
                          onClick={() => handleCopyMessage(msg.content, msg.id)}
                          className="p-1.5 rounded-lg bg-[var(--t-surface)] border border-[var(--t-border)] shadow-md hover:scale-110 active:scale-95 transition-all text-[var(--t-text-muted)] hover:text-[var(--t-primary)]"
                          title="Copy message"
                        >
                          {copiedId === msg.id ? <CheckCircle2 size={10} className="text-[var(--t-success)]" /> : <Copy size={10} />}
                        </button>
                        {msg.role === 'user' && (
                          <button
                            type="button"
                            onClick={() => handleEditMessage(msg.content)}
                            className="p-1.5 rounded-lg bg-[var(--t-surface)] border border-[var(--t-border)] shadow-md hover:scale-110 active:scale-95 transition-all text-[var(--t-text-muted)] hover:text-[var(--t-primary)]"
                            title="Edit and resend"
                          >
                            <Edit2 size={10} />
                          </button>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 min-w-[140px]">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[8px] font-black px-2 py-0.5 rounded-full bg-[var(--t-primary)] text-[var(--t-on-primary)] uppercase tracking-tighter">
                            {msg.role === 'ai' ? '🤖 OS Bot' : 'You'}
                          </span>
                          {msg.systemLog && (
                            <span className="text-[8px] font-bold text-[var(--t-text-muted)] opacity-50 uppercase tracking-widest truncate max-w-[140px]">
                              {msg.systemLog}
                            </span>
                          )}
                        </div>
                        <div className="whitespace-pre-wrap">
                          {msg.id === storeTypingMessageId ? (
                            <div className="flex flex-col gap-3">
                              <div className="">
                                <FormattedContent content={aiDisplayedText} searchQuery={searchQuery} />
                              </div>
                              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--t-primary)]/10 rounded-full w-fit border border-[var(--t-primary)]/20 shadow-sm">
                                <span className="w-1.5 h-1.5 bg-[var(--t-primary)] rounded-full animate-bounce [animation-duration:0.8s]" />
                                <span className="w-1.5 h-1.5 bg-[var(--t-primary)] rounded-full animate-bounce [animation-delay:0.2s] [animation-duration:0.8s]" />
                                <span className="w-1.5 h-1.5 bg-[var(--t-primary)] rounded-full animate-bounce [animation-delay:0.4s] [animation-duration:0.8s]" />
                              </div>
                            </div>
                          ) : (
                            <FormattedContent content={msg.content} searchQuery={searchQuery} />
                          )}
                        </div>

                        {msg.intent === 'disambiguation' && (msg.data as DisambiguationData)?.originalIntent && (
                          <div className="mt-4 flex gap-2">
                            <button
                              onClick={() => {
                                const matched = (msg.data as DisambiguationData).originalIntent;
                                handleExecuteAction(matched.intent.action, matched.params);
                                setAiMessagesForThread('widget', (prev) => prev.map(msgItem => msgItem.id === msg.id ? { ...msgItem, intent: 'resolved' } : msgItem));
                              }}
                              className="px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest bg-[var(--t-primary)] text-white hover:scale-105 transition-all shadow-md shadow-[var(--t-primary-dim)]"
                            >
                              Proceed
                            </button>
                            <button
                              onClick={() => {
                                addAiMessage('widget', {
                                  id: Date.now().toString(),
                                  role: 'ai',
                                  content: "No problem! How can I help then?",
                                  timestamp: new Date().toISOString()
                                });
                                setAiMessagesForThread('widget', (prev) => prev.map(msgItem => msgItem.id === msg.id ? { ...msgItem, intent: 'resolved' } : msgItem));
                              }}
                              className="px-4 py-1.5 bg-[var(--t-surface-hover)] text-[var(--t-text-muted)] border border-[var(--t-border)] rounded-xl text-[10px] font-bold"
                            >
                              No
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {loading && (
              <div className="py-4">
                {null}
              </div>
            )}
          </div>

            {showScrollButton && (
              <button
                onClick={scrollToBottom}
                className="absolute bottom-24 right-4 z-[60] w-10 h-10 rounded-full shadow-2xl flex items-center justify-center hover:scale-110 active:scale-95 transition-all"
                style={{ 
                  background: '#ffffff', // High contrast white background
                  color: '#000000',    // Black icon for visibility
                  border: '2px solid var(--t-primary)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
                }}
              >
                <ArrowDown size={20} />
              </button>
            )}

          <form onSubmit={handleSubmit} className={`p-3 border-t flex flex-col gap-2 ${position.y < 200 ? 'order-2 border-t-0' : ''}`}
            style={{ background: 'var(--t-surface-hover)', borderColor: 'var(--t-border)' }}
          >
            {/* Quick Actions */}
            <div className="flex gap-2 px-1 pb-1 overflow-x-auto scrollbar-none">
              {(() => {
                const memory = getMemory();
                const activeLead = memory.entityStack.find(e => e.type === 'lead');
                if (!activeLead) return null;

                return (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setPrompt(`text ${activeLead.name}`);
                        // Small delay to ensure prompt state is updated if needed, 
                        // though in React we should probably call a handler directly.
                        // For now, let's just trigger submit.
                        setTimeout(() => handleSubmit(), 10);
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--t-primary-dim)] border border-[var(--t-primary)]/20 text-[var(--t-primary)] text-[10px] font-bold hover:scale-105 transition-all shadow-sm shrink-0"
                    >
                      <MessageSquare size={12} /> SMS {activeLead.name.split(' ')[0]}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPrompt(`add task for ${activeLead.name}`);
                        setTimeout(() => handleSubmit(), 10);
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--t-surface)] border border-[var(--t-border)] text-[var(--t-text)] text-[10px] font-bold hover:scale-105 transition-all shadow-sm shrink-0"
                    >
                      <CheckCircle2 size={12} /> Task
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPrompt(`add note for ${activeLead.name}`);
                        setTimeout(() => handleSubmit(), 10);
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--t-surface)] border border-[var(--t-border)] text-[var(--t-text)] text-[10px] font-bold hover:scale-105 transition-all shadow-sm shrink-0"
                    >
                      <FileText size={12} /> Note
                    </button>
                  </>
                );
              })()}
            </div>

            <div className="flex gap-2 w-full relative">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={isAiGenerating ? "Generating response..." : (isRecording ? "Listening..." : "Ask anything...")}
                  className="w-full border rounded-lg pl-3 pr-8 py-2 text-xs outline-none focus:ring-1 transition-all"
                  style={{
                    background: 'var(--t-background)',
                    borderColor: isRecording ? 'var(--t-primary)' : 'var(--t-border)',
                    color: 'var(--t-text)',
                    // @ts-expect-error custom prop
                    '--tw-ring-color': 'var(--t-primary)'
                  }}
                  disabled={loading && !isAiGenerating}
                />
                <button
                  type="button"
                  onClick={toggleRecording}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 transition-colors ${isRecording ? 'animate-pulse' : ''}`}
                  style={{ color: isRecording ? 'var(--t-primary)' : 'var(--t-text-muted)' }}
                  disabled={isAiGenerating}
                >
                  <Mic size={14} />
                </button>
              </div>
              <button
                type="submit"
                disabled={(!isAiGenerating && !loading && !prompt.trim() && !lastUserPrompt)}
                className={`p-2.5 rounded-xl disabled:opacity-30 transition-all AIBotWidget-send-button shadow-lg flex items-center justify-center ${isAiGenerating || loading ? 'animate-pulse' : 'hover:scale-105 active:scale-95'}`}
                style={{ 
                  background: (isAiGenerating || loading) ? '#ef4444' : '#ffffff', // Force red for stop, white for send
                  color: '#000000', // Force black icon
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}
              >
                {isAiGenerating || loading ? <Square size={16} fill="currentColor" /> : <Send size={16} />}
              </button>
            </div>
          </form>
        </div>
      )}



      {/* Floating Bubble Button */}
      {!isAiOpen && (
        <button
          onMouseDown={startDrag}
          onClick={(e) => {
            const moved = Math.abs(e.clientX - dragStart.current.x) > 5 || Math.abs(e.clientY - dragStart.current.y) > 5;
            if (moved) return;

            if (isAiDocked) {
              setAiDocked(false);
              setAiOpen(true);
            } else {
              setAiOpen(true);
            }
          }}
          className={`pointer-events-auto p-4 rounded-full shadow-2xl transition-all duration-200 hover:scale-110 active:scale-95 flex items-center justify-center group relative ${isAiDocked ? 'scale-75 opacity-50 hover:opacity-100 hover:scale-100' : ''}`}
          style={{
            background: isAiDocked ? 'var(--t-surface-active)' : 'var(--t-primary)',
            color: isAiDocked ? 'var(--t-primary)' : 'var(--t-on-primary)',
            transition: 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
            zIndex: 1000,
            ...(isAiDocked ? {
              bottom: 'calc(100vh - 110px)',
              right: sidebarOpen ? '420px' : '320px',
              transform: 'scale(0.6) rotate(-10deg)',
              opacity: 0.4
            } : {})
          }}
        >
          {isAiDocked ? <LayoutIcon size={24} /> : <Bot size={24} />}
          
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-[var(--t-error)] border-2 border-[var(--t-background)] rounded-full animate-pulse" />
          
          <div className="absolute right-full mr-3 whitespace-nowrap bg-[var(--t-surface)] text-[var(--t-text)] text-xs px-2 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none border border-[var(--t-border)] shadow-xl">
            How can I help you, {currentUser?.email?.split('@')[0]}?
          </div>
        </button>
      )}

      <RateLimitModal
        isOpen={showRateLimitModal}
        onClose={() => setShowRateLimitModal(false)}
      />

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />
      <SaveLeadModal
        isOpen={saveContactModal.isOpen}
        phone={saveContactModal.phone}
        onClose={() => setSaveContactModal(prev => ({ ...prev, isOpen: false }))}
      />

      <UsageLimitModal
        isOpen={showUsageModal}
        onClose={() => setShowUsageModal(false)}
        tier={usage?.tier || 'Free'}
        limit={usage?.limit || 10}
      />

      <AIOnboardingModal
        isOpen={showOnboarding}
        onClose={handleCloseOnboarding}
        onConfigureCloud={handleConfigureCloud}
      />
    </div>
  );
}

